import { TextButton } from '@creditornot/cb-button';
import { Plus } from '@creditornot/cb-icons';
import { cssThemedValue } from '@creditornot/cb-ingredients';
import React, { useState } from 'react';
import { FormattedMessage } from 'react-intl';
import styled from 'styled-components';

import { Typography as T } from 'common/typography';

import ApiAccessIntroduction from './api-access/ApiAccessIntroduction';
import MenuApiUserModal from './api-access/MenuApiUserModal';
import OrderApiManageApiKeyModal from './api-access/OrderApiManageApiKeyModal';
import OrderApiSetupModal from './api-access/OrderApiSetupModal';
import OrderApiWebhook from './api-access/OrderApiWebhook';
import VenuesWithApiAccess from './api-access/VenuesWithApiAccess';
import { useMenuApiUsers, useOrderApiCredentials } from './hooks';

type Props = {
  merchantId: string;
};

const Root = styled.div`
  width: 100%;
`;

const Header = styled.div`
  display: flex;
  justify-content: space-between;
  padding: 26px 24px;
  color: ${cssThemedValue('textPrimary')};
  border-block-end: 1px solid ${cssThemedValue('borderPrimary')};
`;

const HeaderActionButton = styled(TextButton)`
  margin: 0 8px;
`;

type ApiKeyHeaderButtonProps = {
  isEditMode: boolean;
  onClick: () => void;
};

const ApiKeyHeaderButton = ({ isEditMode, onClick }: ApiKeyHeaderButtonProps) => {
  return (
    <HeaderActionButton variant={isEditMode ? 'red' : 'blue'} onClick={onClick}>
      {isEditMode ? (
        <FormattedMessage id="merchant-integrations.order-api.reset-api-key-header" />
      ) : (
        <FormattedMessage id="merchant-integrations.order-api.generate-api-key-header" />
      )}
    </HeaderActionButton>
  );
};

const MenuApiHeaderButton = ({ isEditMode, onClick }: ApiKeyHeaderButtonProps) => {
  return (
    <HeaderActionButton icon={!isEditMode ? <Plus /> : null} onClick={onClick}>
      {isEditMode ? (
        <FormattedMessage id="merchant-integrations.menu-api.reset" />
      ) : (
        <FormattedMessage id="merchant-integrations.menu-api.setup" />
      )}
    </HeaderActionButton>
  );
};

const ApiManagement = ({ merchantId }: Props) => {
  const [menuApiModalOpen, setMenuApiModalOpen] = useState(false);
  const [orderApiModalOpen, setOrderApiModelOpen] = useState(false);
  const [manageApiKeyModalOpen, setManageApiKeyModalOpen] = useState(false);
  const {
    metadata: menuApiMetadata,
    isLoading: isLoadingMenuApi,
    refresh: refreshMenuApi,
  } = useMenuApiUsers(merchantId);
  const {
    credentials: orderApiMetadata,
    isLoading: isLoadingOrderApi,
    refresh: refreshOrderApi,
  } = useOrderApiCredentials(merchantId);

  const hasAtLeastOneApi = menuApiMetadata?.username || orderApiMetadata;

  return (
    <Root>
      <Header>
        <T.Heading6>
          <FormattedMessage id="merchant-integrations.menu-api-access-header" />
        </T.Heading6>
        <T.Heading6>
          {hasAtLeastOneApi && (
            <>
              {orderApiMetadata ? (
                <ApiKeyHeaderButton
                  isEditMode={!!orderApiMetadata?.api_key_id}
                  onClick={() => setManageApiKeyModalOpen(true)}
                />
              ) : (
                <HeaderActionButton icon={<Plus />} onClick={() => setOrderApiModelOpen(true)}>
                  <FormattedMessage id="merchant-integrations.order-api.setup" />
                </HeaderActionButton>
              )}
              <MenuApiHeaderButton isEditMode={!!menuApiMetadata?.username} onClick={() => setMenuApiModalOpen(true)} />
            </>
          )}
        </T.Heading6>
      </Header>
      {!isLoadingMenuApi &&
        !isLoadingOrderApi &&
        (hasAtLeastOneApi ? (
          <VenuesWithApiAccess
            menuApiMetadata={menuApiMetadata}
            orderApiMetadata={orderApiMetadata}
            merchantId={merchantId}
          />
        ) : (
          <ApiAccessIntroduction onOpenMenuApiModal={setMenuApiModalOpen} onOpenOrderApiModal={setOrderApiModelOpen} />
        ))}
      {!isLoadingMenuApi && (
        <MenuApiUserModal
          credentials={menuApiMetadata}
          isOpen={menuApiModalOpen}
          onClose={() => {
            setMenuApiModalOpen(false);
            if (!menuApiMetadata) {
              refreshMenuApi();
            }
          }}
          merchantId={merchantId}
        />
      )}
      {orderApiMetadata ? (
        <>
          <OrderApiManageApiKeyModal
            orderApiMetadata={orderApiMetadata}
            isOpen={manageApiKeyModalOpen}
            onClose={() => {
              setManageApiKeyModalOpen(false);
              refreshOrderApi();
            }}
            merchantId={merchantId}
          />
          <OrderApiWebhook merchantId={merchantId} orderApiMetadata={orderApiMetadata} />
        </>
      ) : (
        <OrderApiSetupModal
          isOpen={orderApiModalOpen}
          onClose={() => {
            setOrderApiModelOpen(false);
            if (!orderApiMetadata) {
              refreshOrderApi();
            }
          }}
          merchantId={merchantId}
        />
      )}
    </Root>
  );
};

export default ApiManagement;
